import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import {
  Box, Stack, Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

import { LinkButton } from '../../../../components/Button';
import colors from '../../../../styles/colors';
import { validateImage } from '../utils/uploadCampaignImage';
import { imageUploadMessage } from '../utils/errorMessages';
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGE_KB } from '../utils/constants';
import texts from '../texts.json';
import { Dropzone, HiddenInput } from './style';

const UPLOAD_HINT = format(texts.sections.image.uploadHint, { maxKb: MAX_IMAGE_KB });

const formatKilobytes = (bytes) => `${Math.max(1, Math.round(bytes / 1024))} KB`;

const ImagePicker = ({
  image, onChange, onError, note,
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);

  const previewUrl = image?.previewUrl;
  useEffect(() => {
    if (!previewUrl) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const displayUrl = previewUrl || image?.url;

  const triggerPick = () => {
    inputRef.current?.click();
  };

  const handleFile = (file) => {
    if (!file) return;
    try {
      validateImage(file);
    } catch (err) {
      setError(imageUploadMessage(err));
      onError?.(true);
      return;
    }
    setError(null);
    onError?.(false);
    onChange({
      file,
      previewUrl: URL.createObjectURL(file),
      fileName: file.name,
      sizeBytes: file.size,
    });
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      triggerPick();
    }
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
    onError?.(false);
  };

  return (
    <>
      {displayUrl ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component="img"
            src={displayUrl}
            alt={image.fileName || texts.sections.image.alt}
            sx={{
              width: 96,
              height: 96,
              objectFit: 'cover',
              borderRadius: 1,
              border: `1px solid ${colors.shades.gamma3}`,
            }}
          />
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{image.fileName}</Typography>
            {!!image.sizeBytes && (
              <Typography variant="caption" color="text.secondary">
                {formatKilobytes(image.sizeBytes)}
              </Typography>
            )}
            <LinkButton onClick={handleRemove}>
              {texts.sections.image.remove}
            </LinkButton>
          </Stack>
        </Stack>
      ) : (
        <Dropzone
          dragging={dragging}
          hasError={!!error}
          onClick={triggerPick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: colors.shades.gamma10 }} />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1, color: colors.shades.beta2 }}>
            {texts.sections.image.uploadCta}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {UPLOAD_HINT}
          </Typography>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {error}
            </Typography>
          )}
        </Dropzone>
      )}
      {note && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          {note}
        </Typography>
      )}
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        onChange={handleInputChange}
      />
    </>
  );
};

ImagePicker.propTypes = {
  image: PropTypes.shape({
    file: PropTypes.instanceOf(File),
    previewUrl: PropTypes.string,
    url: PropTypes.string,
    fileName: PropTypes.string,
    sizeBytes: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  note: PropTypes.string,
};

ImagePicker.defaultProps = { image: null, onError: null, note: null };

export default ImagePicker;
